// What a delivery amounts to, in the few words a card has room for. The words are translation
// keys; the technical names in between are not words and stay as they are.

import type { ActivityDelivery, ActivityStream, PlaybackMethod } from '../../api/types';
import { channelLayout, codecName, formatBitrate } from '../player/streamText';

/// Direct Play is the goal and shows as success; a remux costs a worker but no encoder; a
/// transcode costs the GPU and is what an administrator looks for first.
export const METHOD_COLOR: Record<PlaybackMethod, 'success' | 'info' | 'warning'> = {
  DirectPlay: 'success',
  DirectStream: 'info',
  HardwareTranscode: 'warning',
};

const KIND_KEY: Record<ActivityStream['kind'], string> = {
  Video: 'video',
  Audio: 'audio',
  Subtitle: 'subtitle',
};

/// Which stream decided what a kind of stream amounts to: one that is transcoded beats one that
/// is copied, and one that is skipped says nothing.
const ACTION_WEIGHT: Record<ActivityStream['action'], number> = {
  Transcode: 3,
  Extract: 2,
  Copy: 1,
  Discard: 0,
  Drop: 0,
};

/// "Video transcoded · Audio copied", as translation keys - one per kind of stream that is
/// delivered at all, in the order a person reads a film: picture, sound, words.
export function summaryKeys(delivery: ActivityDelivery): string[] {
  const keys: string[] = [];

  for (const kind of ['Video', 'Audio', 'Subtitle'] as const) {
    const deciding = delivery.streams
      .filter((stream) => stream.kind === kind && ACTION_WEIGHT[stream.action] > 0)
      .sort((a, b) => ACTION_WEIGHT[b.action] - ACTION_WEIGHT[a.action])[0];

    if (deciding) {
      keys.push(`sessions.summary.${KIND_KEY[kind]}${deciding.action}`);
    }
  }

  return keys;
}

/// Whether anything about a stream changes on its way to the client.
export function isChanged(stream: ActivityStream): boolean {
  return stream.action === 'Transcode' || stream.action === 'Extract';
}

/// What a changed stream becomes, in one line: "H.264 · 1920×1080 · 8.0 Mbit/s", "AAC · 5.1".
export function describeTarget(stream: ActivityStream, delivery: ActivityDelivery): string {
  const parts = [codecName(stream.targetCodec)];

  if (stream.kind === 'Video') {
    if (stream.renditions.length > 1) {
      // A ladder: every version the client may change between.
      parts.push(stream.renditions.map((rendition) => `${rendition.height}p`).join(' / '));
    } else if (stream.targetWidth && stream.targetHeight) {
      parts.push(`${stream.targetWidth}×${stream.targetHeight}`);
    } else if (stream.source?.width && stream.source.height) {
      parts.push(`${stream.source.width}×${stream.source.height}`);
    }

    if (delivery.toneMap === 'ServerHardware') {
      parts.push('SDR');
    }

    if (stream.targetBitDepth) {
      parts.push(`${stream.targetBitDepth}-bit`);
    }
  }

  if (stream.kind === 'Audio') {
    const channels = stream.targetChannels ?? stream.source?.channels;

    if (channels) {
      parts.push(channelLayout(channels));
    }
  }

  if (stream.targetBitrate) {
    parts.push(formatBitrate(stream.targetBitrate));
  }

  return parts.join(' · ');
}
